import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { Button, Dropdown, DropdownOption, Input, Modal, ModalSize } from '@column/column-ui-kit';

import { TransferSnapshotFilters, useTransferSnapshots } from '../../Transfers/_hooks/useTransferSnapshots';
import { FormField } from '~/components/FormField';
import { NotificationList } from '~/components/NotificationList';
import { useBankAccountDropdown, useWatchEffect } from '~/hooks';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { TransferStatusObject, UserTransferFilterSnapshot } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { ApiError } from '~/typings/API';
import { formatString } from '~/util';

import { TransferAmountFilter } from './TransferSnapshotModal/Amount';
import { Clear } from './TransferSnapshotModal/Clear';
import { TransferDateFilter } from './TransferSnapshotModal/Date';
import { Section } from './TransferSnapshotModal/Section';
import { TransferTypeFilter } from './TransferSnapshotModal/Type';

type TransferStatus = (typeof TransferStatusObject)[keyof typeof TransferStatusObject][number];

interface TransferSnapshotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: Partial<UserTransferFilterSnapshot>) => void;
  onRefresh?: () => void;
  id?: number;
  name?: string;
  filters?: TransferSnapshotFilters;
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  padding: 0 var(--page-margin) 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 16px;
`;

const defaultFilters: TransferSnapshotFilters = {
  bankAccountId: null,
  status: null,
  type: null,
  amount: null,
  amountGte: null,
  amountLte: null,
  createdAtGte: null,
  createdAtLt: null,
  isIncoming: null,
};

export const TransferSnapshotModal: FC<TransferSnapshotModalProps> = ({
  isOpen,
  onClose,
  id,
  name,
  filters,
  onSave,
  onRefresh,
}) => {
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const [sections, setSections] = useState<Record<'type' | 'amount' | 'date', boolean | undefined>>({
    type: undefined,
    amount: undefined,
    date: undefined,
  });
  const { createTransferSnapshot, updateTransferSnapshot, deleteTransferSnapshot } = useTransferSnapshots();

  const { handleSubmitErrors } = useFormErrorHandler({ defaultDisplayType: 'popup' });
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();

  const methods = useForm<Partial<UserTransferFilterSnapshot>>({
    defaultValues: filters
      ? {
          name: name ?? '',
          filters,
        }
      : { filters: defaultFilters },
  });

  const { handleSubmit, setValue, watch, reset } = methods;

  useEffect(() => {
    reset({ name: name ?? '', filters: filters ?? defaultFilters });
    setSections({ type: undefined, amount: undefined, date: undefined });
  }, [filters]);

  useEffect(() => {
    if (isOpen) {
      reset({ name: name ?? '', filters: filters ?? defaultFilters });
      setSections({ type: undefined, amount: undefined, date: undefined });
    }
  }, [isOpen]);

  const { options, hiddenOptions, searchBankAccounts, isLoading } = useBankAccountDropdown(
    filters?.bankAccountId ?? undefined
  );

  const handleSelectStatus = useCallback(
    (status: TransferStatus) => {
      const currentStatus = watch('filters.status');

      const active = currentStatus && typeof currentStatus === 'string' ? currentStatus.split(',') : [];

      if (active.includes(status)) {
        setValue('filters.status', active.filter((s) => s !== status).join(','));
      } else {
        setValue('filters.status', [...active, status].join(','));
      }
    },
    [watch, setValue]
  );

  const statusOptions = useMemo(() => {
    const array: DropdownOption[] = [];

    for (const key in TransferStatusObject) {
      if (TransferStatusObject.hasOwnProperty(key)) {
        array.push({
          label: formatString(key).toUpperCase(),
          isDivider: true,
        });

        const values = TransferStatusObject[key as keyof typeof TransferStatusObject];

        values.forEach((entry) => {
          array.push({
            label: formatString(entry),
            value: entry,
            onClick: () => handleSelectStatus(entry),
          });
        });
      }
    }

    return array;
  }, [TransferStatusObject]);

  const onSuccess = useCallback(
    async (data: Partial<UserTransferFilterSnapshot>) => {
      if (isButtonLoading) {
        return;
      }

      setIsButtonLoading(true);

      if (!data?.name) {
        addDangerNotification({
          content: 'Please enter a name for the snapshot',
          display: 'popup',
        });

        setIsButtonLoading(false);

        return;
      }

      try {
        if (typeof id !== 'undefined' && data.filters) {
          await updateTransferSnapshot(id, data.name, data.filters);

          addSuccessNotification({
            content: `Transfer snapshot ${data.name} updated successfully`,
          });
        } else if (data.filters) {
          await createTransferSnapshot(data.name, data.filters);

          addSuccessNotification({
            content: `Transfer snapshot ${data.name} created successfully`,
          });
        }

        if (onSave) {
          onSave({
            ...data,
            filters: data.filters,
          });
        }

        onClose();
      } catch (error) {
        addDangerNotification({
          content: (error as ApiError).message,
          display: 'popup',
        });
      } finally {
        setIsButtonLoading(false);
      }
    },
    [onClose, isButtonLoading, id, updateTransferSnapshot, createTransferSnapshot, onSave]
  );

  useWatchEffect(() => {
    if (sections.type === undefined && (watch('filters.isIncoming') || watch('filters.type'))) {
      setSections((s) => ({ ...s, type: true }));
    }

    if (
      sections.amount === undefined &&
      (watch('filters.amountGte') || watch('filters.amountLte') || watch('filters.amount'))
    ) {
      setSections((s) => ({ ...s, amount: true }));
    }

    if (sections.date === undefined && (watch('filters.createdAtGte') || watch('filters.createdAtLt'))) {
      setSections((s) => ({ ...s, date: true }));
    }
  }, [watch('filters')]);

  const handleDelete = useCallback(() => {
    if (typeof id === 'number') {
      deleteTransferSnapshot(id)
        .then(() => {
          addSuccessNotification({
            content: `Transfer snapshot ${name} deleted successfully`,
          });

          onClose();

          onRefresh?.();
        })
        .catch((error) => {
          addDangerNotification({
            content: (error as ApiError).message,
            display: 'popup',
          });
        });
    }
  }, [id, deleteTransferSnapshot, onRefresh]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.Medium} withClose={false}>
      <Modal.Header title="Transfer Snapshot">
        <Modal.Close variant="subtle" size="small" onClick={onClose} />
      </Modal.Header>

      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSuccess, (errors) => handleSubmitErrors<UserTransferFilterSnapshot>(errors))}>
          <Content>
            <div>
              <NotificationList display="popup" />

              <FormField<UserTransferFilterSnapshot> id="name" label="Name" oneColumn rules={{ required: true }}>
                {({ value, onChange, onBlur }, { isTouched, error }) => (
                  <Input
                    hasError={isTouched && !!error}
                    value={String(value ?? '')}
                    placeholder="Snapshot Name"
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              </FormField>
            </div>

            <FormField<UserTransferFilterSnapshot> id="filters.bankAccountId" label="Bank Account" oneColumn>
              {({ value, onChange, onBlur }) => (
                <Clear
                  isActive={!!watch('filters.bankAccountId')}
                  onClear={() => onChange(null)}
                  tooltipContent="Reset Bank Account"
                >
                  <Dropdown
                    options={options}
                    hiddenOptions={hiddenOptions}
                    active={value}
                    fullWidth
                    maxWidth="640px"
                    search
                    searchLabel="Search for description"
                    onSearchChange={(description: string) => searchBankAccounts({ description })}
                    onChange={onChange}
                    onOpenChange={(open) => !open && onBlur()}
                    isLoading={isLoading}
                  />
                </Clear>
              )}
            </FormField>

            <FormField<UserTransferFilterSnapshot> id="filters.status" label="Status" oneColumn>
              {({ value, onBlur }) => (
                <Clear
                  isActive={!!watch('filters.status')}
                  onClear={() => setValue('filters.status', '')}
                  tooltipContent="Reset Status"
                >
                  <Dropdown
                    options={statusOptions}
                    label={
                      value && typeof value === 'string'
                        ? [...new Set(value.split(','))].map((s) => formatString(s)).join(', ')
                        : undefined
                    }
                    active={value && typeof value === 'string' ? value.split(',') : []}
                    fullWidth
                    search
                    searchLabel="Search..."
                    onOpenChange={(open) => !open && onBlur()}
                  />
                </Clear>
              )}
            </FormField>
          </Content>

          <Section
            title="Type"
            isOpen={sections.type ?? false}
            onOpenChange={(isOpenSection) => setSections({ ...sections, type: isOpenSection })}
            onFilterClear={() => {
              setValue('filters.type', null);
              setValue('filters.isIncoming', null);
              setSections((s) => ({ ...s, type: false }));
            }}
            isClearable={!!watch('filters.type') || watch('filters.isIncoming') !== null}
          >
            <TransferTypeFilter />
          </Section>

          <Section
            title="Amount"
            isOpen={sections.amount ?? false}
            onOpenChange={(isOpenSection) => setSections({ ...sections, amount: isOpenSection })}
            onFilterClear={() => {
              setValue('filters.amountGte', null);
              setValue('filters.amountLte', null);
              setValue('filters.amount', null);
              setSections((s) => ({ ...s, amount: false }));
            }}
            isClearable={!!watch('filters.amountGte') || !!watch('filters.amountLte') || !!watch('filters.amount')}
          >
            <TransferAmountFilter />
          </Section>

          <Section
            title="Date"
            isOpen={sections.date ?? false}
            onOpenChange={(isOpenSection) => setSections({ ...sections, date: isOpenSection })}
            onFilterClear={() => {
              setValue('filters.createdAtGte', null);
              setValue('filters.createdAtLt', null);
              setSections((s) => ({ ...s, date: false }));
            }}
            isClearable={!!watch('filters.createdAtGte') || !!watch('filters.createdAtLt')}
          >
            <TransferDateFilter />
          </Section>

          <Modal.Footer $withBackground>
            <Buttons>
              <Button variant="secondary" onClick={onClose} size="small" type="button">
                Cancel
              </Button>
              {typeof id !== 'undefined' && (
                <Button variant="danger" onClick={handleDelete} size="small" type="button">
                  Delete
                </Button>
              )}
            </Buttons>
            <Button isLoading={isButtonLoading} size="small">
              Save changes
            </Button>
          </Modal.Footer>
        </Form>
      </FormProvider>
    </Modal>
  );
};
